import * as utils from '@/utils.js'
import i18n from '@/i18n/i18n.js'


const REPORT_DEFAULT_DAYS_BACK = 60

function toLocalDateInput (date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().substring(0, 10)
}

export function getDefaultReportDateRange (daysBack = REPORT_DEFAULT_DAYS_BACK) {
  const today = new Date()
  const start = new Date(today)
  start.setDate(today.getDate() - daysBack)
  return {
    start: toLocalDateInput(start),
    end: toLocalDateInput(today),
  }
}

export function createReportTextComparator (locale = 'en') {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true })
  return (a, b) => collator.compare(String(a ?? ''), String(b ?? ''))
}

export function createReportNumberComparator () {
  const toNumber = (value) => {
    if (value == null || value === '') return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }

  return (a, b) => {
    const na = toNumber(a)
    const nb = toNumber(b)
    if (na == null && nb == null) return 0
    if (na == null) return 1
    if (nb == null) return -1
    return na - nb
  }
}

export function createReportDateComparator () {
  const toTimestamp = (value) => {
    if (value == null || value === '') return null
    const n = Number(value)
    if (Number.isFinite(n)) return n
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return (a, b) => {
    const ta = toTimestamp(a)
    const tb = toTimestamp(b)
    if (ta == null && tb == null) return 0
    if (ta == null) return 1
    if (tb == null) return -1
    return ta - tb
  }
}

export function createReportOperationTypeComparator (operationTypes, locale = 'en') {
  const compareText = createReportTextComparator(locale)
  return (a, b) => {
    const labelA = String(operationTypes?.[a] || a || '')
    const labelB = String(operationTypes?.[b] || b || '')
    return compareText(labelA, labelB)
  }
}

export function parseReportNumber (value) {
  if (value == null || value === '') return null
  const n = Number(String(value).replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

export function normalizeReportTimestamp (value) {
  if (value == null) return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return n > 1e12 ? n : n * 1000
}

export function formatReportTimelineDate (value) {
  if (value == null || value === '') return '-'
  if (typeof value === 'string' && /[A-Za-z]/.test(value)) {
    const formatted = utils.formatDateAndTimeFromString(value)
    return formatted === 'Invalid Date' ? '-' : formatted
  }
  const n = Number(value)
  if (Number.isFinite(n)) return utils.formatTimeAndDate(n)
  const formatted = utils.formatDateAndTimeFromString(value)
  return formatted === 'Invalid Date' ? '-' : formatted
}

export function formatReportMaturityDate (date) {
  return utils.formatMaturityDate(date)
}

export function getAcceptedReportRate (item) {
  const quoteList = item?.rfq_lastquote?.quote_list ?? []
  const accepted = quoteList.find(q => q?.deal)
  const rate = parseReportNumber(accepted?.quote_bank_rate)
  return rate != null ? rate : parseReportNumber(item?.accepted_rate)
}

export function hasAcceptedReportRate (item) {
  return getAcceptedReportRate(item) != null
}

export function getReconciledOriginalRate (row) {
  const pxo = row?.rfq_reconcil_sign?.rfq_reconcil_pxo
  return pxo != null ? parseReportNumber(pxo) : null
}

// Downloads for a report row are locked for this long after the deal/RFQ
// reference time, unless the price gets reconciled first (see isReportReconciled).
// The lock is derived from a server-provided timestamp already on the row, so it
// naturally survives refresh/logout/login without any separate persistence layer.
export const REPORT_DOWNLOAD_LOCK_MS = 10 * 60 * 1000

function toEpochMs (value) {
  if (value == null || value === '') return null
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return n > 1e12 ? n : n * 1000
}

export function getReportReferenceTime (row, isOperationMode) {
  if (isOperationMode) {
    return toEpochMs(row?.rfq_timecreate) ?? toEpochMs(row?.timecreate)
  }
  return toEpochMs(row?.timecreate) ?? toEpochMs(row?.rfq_deal_time)
}

export function isReportReconciled (row) {
  return Boolean(row?.rfq_reconcil_sign)
}

export function getReportLockRemainingMs (row, isOperationMode, now = Date.now()) {
  if (isReportReconciled(row)) return 0
  const refTime = getReportReferenceTime(row, isOperationMode)
  if (refTime == null) return 0
  return Math.max(0, REPORT_DOWNLOAD_LOCK_MS - (now - refTime))
}

export function isReportDownloadLocked (row, isOperationMode, now = Date.now()) {
  return getReportLockRemainingMs(row, isOperationMode, now) > 0
}

export function normalizeOperationReportRow (row) {
  const quoteList = row?.rfq_lastquote?.quote_list ?? []
  const bestRateVal = parseReportNumber(row.best_rate)
  const finalRate = parseReportNumber(row.rfq_final_rate)
  const volumeBrl = parseReportNumber(row.volume_brl)
  const companyName = row.company?.name || row.company?.id || ''

  return {
    ...row,
    company_name: companyName,
    timecreate:     normalizeReportTimestamp(row.timecreate),
    rfq_timecreate: normalizeReportTimestamp(row.rfq_timecreate),
    rfq_final_rate: finalRate,
    rfq_px:         finalRate,
    rfq_reconcil_pxo: getReconciledOriginalRate(row),
    volume_brl:     volumeBrl,
    best_rate:      bestRateVal != null ? bestRateVal.toFixed(4) : '',
    rfq_lastquote: {
      ...(row.rfq_lastquote || {}),
      quote_list: quoteList,
    },
  }
}
function getRowOperationAlias (row) {
  return row.operation_alias ?? row.operationAlias ?? row.operation?.alias ?? null
}

function getRowOperationType (row) {
  return row.operation_type ?? row.operationType ?? row.operation?.operationType ?? row.operation?.type ?? null
}

export function normalizeRfqReportRow (row) {
  const quoteList = row?.rfq_lastquote?.quote_list ?? []
  const bestRateVal = parseReportNumber(row.best_rate)
  const finalRate = parseReportNumber(row.rfq_final_rate)
  const volumeBrl = parseReportNumber(row.volume_brl)
  const companyName = row.company?.name || row.company?.id || ''

  return {
    ...row,
    company_name:   companyName,
    rfq_final_rate: finalRate,
    rfq_px:         finalRate,
    rfq_reconcil_pxo: getReconciledOriginalRate(row),
    volume_brl:     volumeBrl,
    best_rate:      bestRateVal != null ? bestRateVal.toFixed(4) : '',
    operation_alias: getRowOperationAlias(row),
    operation_type:  getRowOperationType(row),
    rfq_lastquote: {
      ...(row.rfq_lastquote || {}),
      quote_list: quoteList,
    },
  }
}

export function translateStatus (status) {
  if (!status) return '-'
  const cleanedStatus = String(status).toLowerCase().replace(/[\s_]+/g, '')
  const path = `broker.status.${cleanedStatus}`
  if (i18n.te(path)) {
    return i18n.t(path).toUpperCase()
  }
  return String(status).toUpperCase()
}

const QUOTE_STATUS_BY_RFQ_STATUS = {
  QUOTE: 'pending',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
}

// A bank's quote has no outcome of its own: it follows the RFQ's. `quote.deal`
// only marks the winner, and is false on every quote of an RFQ that is still
// open or was cancelled, so it cannot tell "declined" from "not decided yet".
// Once the RFQ deals, accepting one bank's quote is declining all the others.
// A cancelled RFQ does not record why, so an expired one reads as cancelled.
export function getReportQuoteStatus (quote, rfqStatus) {
  const status = String(rfqStatus || '').toUpperCase()
  if (status === 'DEAL') return quote?.deal ? 'accepted' : 'declined'
  return QUOTE_STATUS_BY_RFQ_STATUS[status] || null
}

export function formatReportQuoteStatus (quote, rfqStatus) {
  const status = getReportQuoteStatus(quote, rfqStatus)
  return status ? i18n.t(`reports.expand.rates.${status}`).toUpperCase() : '-'
}
